import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Star, Waves, Users, BedDouble, CalendarDays, ChevronDown } from "lucide-react";
import { useBooking, VILLA } from "../data";

const line = (delay) => ({
    hidden: { y: "115%" },
    show: { y: "0%", transition: { duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] } },
});

export default function Hero() {
    const ref = useRef(null);
    const { openBooking } = useBooking();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section ref={ref} id="hero" className="relative min-h-[100svh] overflow-hidden grain" data-testid="hero-section">
            <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
                <video autoPlay muted loop playsInline preload="auto" poster="/images/hero.jpg"
                    className="w-full h-full object-cover" data-testid="hero-background-video">
                    <source src="/images/hero-video.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-b from-[#0F2837]/70 via-[#0F2837]/35 to-[#0F2837]/80" />
            </motion.div>

            <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pt-40 lg:pt-48 pb-24 flex flex-col min-h-[100svh]">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }}
                    className="flex items-center gap-2 text-white/85 text-xs tracking-[0.3em] uppercase" data-testid="hero-location-eyebrow">
                    <MapPin size={14} className="text-[#C49A45]" /> Fernandina Beach · Amelia Island, Florida
                </motion.div>

                <motion.h1 initial="hidden" animate="show" className="font-serif-d font-light text-white mt-6 text-[13vw] sm:text-6xl lg:text-[5.4rem] leading-[1.02] tracking-tight max-w-4xl" data-testid="hero-title">
                    <span className="block overflow-hidden"><motion.span variants={line(0.55)} className="block">Stay near the shore.</motion.span></span>
                    <span className="block overflow-hidden"><motion.span variants={line(0.75)} className="block italic text-[#E9D9AE]">Live the island.</motion.span></span>
                </motion.h1>

                <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9 }}
                    className="mt-6 max-w-xl text-white/80 text-base lg:text-lg font-light leading-relaxed" data-testid="hero-subtitle">
                    A premium three-bedroom coastal retreat at {VILLA.name} {VILLA.unit} — three minutes from the sand, with a private balcony and a sun-drenched community pool.
                </motion.p>

                <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35, duration: 0.9 }}
                    className="mt-8 flex flex-wrap items-center gap-3" data-testid="hero-quick-facts">
                    {[
                        { icon: BedDouble, text: "3 BR · 2.5 BA" },
                        { icon: Users, text: "Sleeps 6" },
                        { icon: Waves, text: "3-min walk to beach" },
                        { icon: Star, text: "10/10 Exceptional" },
                    ].map(({ icon: Icon, text }) => (
                        <span key={text} className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-md px-4 py-2 text-[13px] text-white">
                            <Icon size={14} className="text-[#C49A45]" /> {text}
                        </span>
                    ))}
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.55, duration: 1 }}
                    className="mt-auto pt-12" data-testid="hero-booking-search-bar">
                    <div className="max-w-3xl rounded-3xl bg-white/95 backdrop-blur-xl shadow-[0_30px_60px_-20px_rgba(15,40,55,0.5)] p-4 sm:p-5 flex flex-col sm:flex-row items-stretch gap-3">
                        <div className="flex-1 flex items-center gap-3 rounded-2xl bg-[#F3EDE2] px-4 py-3">
                            <CalendarDays size={18} className="text-[#C49A45] shrink-0" />
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A78]">Your dates</p>
                                <p className="text-sm font-semibold text-[#0F2837]">Select check-in & check-out</p>
                            </div>
                        </div>
                        <div className="flex-1 flex items-center gap-3 rounded-2xl bg-[#F3EDE2] px-4 py-3">
                            <Users size={18} className="text-[#C49A45] shrink-0" />
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A78]">Guests</p>
                                <p className="text-sm font-semibold text-[#0F2837]">Up to 6 guests</p>
                            </div>
                        </div>
                        <button onClick={() => openBooking()} data-testid="search-stays-button"
                            className="btn-primary-lux rounded-2xl px-8 py-4 text-sm font-semibold tracking-wide whitespace-nowrap">
                            Check Availability →
                        </button>
                    </div>
                    <p className="mt-3 text-white/60 text-xs" data-testid="hero-rate-note">From $624 / night · You will not be charged yet</p>
                </motion.div>
            </motion.div>

            <motion.button onClick={() => document.getElementById("overview")?.scrollIntoView({ behavior: "smooth" })}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 z-10" data-testid="hero-scroll-indicator" aria-label="Scroll down">
                <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2 }}>
                    <ChevronDown size={26} />
                </motion.div>
            </motion.button>
        </section>
    );
}
